import { overlapsDay } from '../../lib/dates';
import type { Absence, Assignment, Crew, Employee, PlanData, Site } from '../../lib/types';

/**
 * Der Plan wird nach Tagen gedacht: jeder Tag ist eine Spalte und trägt seine
 * eigenen Baustellen als Kacheln, in denen die Namen stehen.
 *
 * Welche Baustelle an einem Tag auftaucht, entscheidet der Plan selbst — eine
 * Baustelle ist entweder ausdrücklich für den Tag eingeplant (`siteDays`) oder
 * es steht schon jemand darauf. So zeigt eine Woche nur, was tatsächlich läuft,
 * statt jede angelegte Baustelle an jedem Tag zu wiederholen.
 */

/** Ein Name in einer Baustellen-Kachel. */
export interface PlanEntry {
  employee: Employee;
  assignment: Assignment;
  /** Gesetzt, wenn die Person an diesem Tag eigentlich abwesend ist. */
  conflict: Absence | null;
}

/** Einteilungen einer Kachel, die zur selben Kolonne gehören. */
export interface TileCrewGroup {
  /** null = Namen ohne Kolonne — sie stehen lose in der Kachel. */
  crew: Crew | null;
  entries: PlanEntry[];
}

/** Eine Baustelle an einem Tag. */
export interface SiteTile {
  site: Site;
  entries: PlanEntry[];
  /** Dieselben Einteilungen, nach Kolonnen gebündelt — für die Rahmen in der Kachel. */
  groups: TileCrewGroup[];
  /** Ausdrücklich für den Tag eingeplant — nicht nur durch Einteilungen sichtbar. */
  planned: boolean;
  /** Freitext-Info, gilt nur für genau diesen Tag ("Beton kommt 7 Uhr"). */
  note: string;
}

/** Ein Tag mit allem, was er trägt. */
export interface DayColumn {
  date: string;
  weekend: boolean;
  holiday: string | null;
  tiles: SiteTile[];
  /** Anzahl eingeteilter Mitarbeiter — eine Person zählt einmal, auch bei zwei Baustellen. */
  staffed: number;
  absent: { employee: Employee; absence: Absence }[];
  unplanned: Employee[];
}

export interface PlanModel {
  columns: DayColumn[];
  employeesById: Map<number, Employee>;
  sitesById: Map<number, Site>;
}

/**
 * Formt die flachen Serverlisten in die Struktur um, die das Brett zeichnet.
 *
 * Bewusst einmal vorab statt in jeder Kachel: bei 60 Mitarbeitern über einen
 * Monat wären das sonst tausende Durchläufe pro Neuzeichnen.
 */
export function buildPlanModel(plan: PlanData, workdaysOnly: boolean): PlanModel {
  const days = plan.days.filter((day) => !workdaysOnly || !day.weekend);
  const employeesById = new Map(plan.employees.map((employee) => [employee.id, employee]));
  const sitesById = new Map(plan.sites.map((site) => [site.id, site]));

  const absencesByEmployee = groupBy(plan.absences, (absence) => absence.employeeId);

  // Abwesenheit je Person und Tag einmal vorab auflösen — sie wird sowohl für
  // den Konflikthinweis als auch für die Restliste gebraucht.
  const absenceLookup = new Map<string, Absence>();
  for (const employee of plan.employees) {
    const own = absencesByEmployee.get(employee.id) ?? [];
    for (const day of days) {
      const hit = own.find((entry) => overlapsDay(entry.startDate, entry.endDate, day.date));
      if (hit) absenceLookup.set(`${employee.id}|${day.date}`, hit);
    }
  }

  const dates = new Set(days.map((day) => day.date));

  /** Einteilungen, aufgeschlüsselt nach Tag und Baustelle. */
  const entriesByCell = new Map<string, PlanEntry[]>();
  const plannedPerDay = new Map<string, Set<number>>(days.map((day) => [day.date, new Set()]));

  for (const assignment of plan.assignments) {
    const employee = employeesById.get(assignment.employeeId);
    if (!employee || !dates.has(assignment.date) || !sitesById.has(assignment.siteId)) continue;

    const entry: PlanEntry = {
      employee,
      assignment,
      conflict: absenceLookup.get(`${employee.id}|${assignment.date}`) ?? null,
    };

    const key = cellKey(assignment.date, assignment.siteId);
    const list = entriesByCell.get(key);
    if (list) list.push(entry);
    else entriesByCell.set(key, [entry]);

    plannedPerDay.get(assignment.date)?.add(employee.id);
  }

  for (const list of entriesByCell.values()) list.sort(byName);

  /** Ausdrücklich eingeplante Baustellen je Tag, samt Tages-Info. */
  const pinnedByDay = new Map<string, Map<number, string>>(days.map((day) => [day.date, new Map()]));
  for (const pin of plan.siteDays ?? []) pinnedByDay.get(pin.date)?.set(pin.siteId, pin.note ?? '');

  const columns: DayColumn[] = days.map((day) => {
    const pinned = pinnedByDay.get(day.date) ?? new Map<number, string>();

    // Die Reihenfolge kommt aus der Baustellenliste (sortOrder) — damit „Büro"
    // und „Bauleiter" an jedem Tag an derselben Stelle stehen. Feste Bereiche
    // (Störungsdienst, Lager, Büro) stehen davor: sie sind jeden Tag im Plan,
    // ohne dass sie jemand einplant.
    const tiles: SiteTile[] = [];
    for (const site of plan.sites) {
      const entries = entriesByCell.get(cellKey(day.date, site.id)) ?? [];
      if (!site.permanent && !pinned.has(site.id) && entries.length === 0) continue;
      tiles.push({
        site,
        entries,
        groups: groupEntriesByCrew(entries, plan.crews),
        planned: site.permanent || pinned.has(site.id),
        note: pinned.get(site.id) ?? '',
      });
    }
    tiles.sort((a, b) => Number(b.site.permanent) - Number(a.site.permanent));

    const staffed = plannedPerDay.get(day.date) ?? new Set<number>();
    const absent: DayColumn['absent'] = [];
    const unplanned: Employee[] = [];

    for (const employee of plan.employees) {
      if (staffed.has(employee.id)) continue;
      const absence = absenceLookup.get(`${employee.id}|${day.date}`);
      if (absence) absent.push({ employee, absence });
      else unplanned.push(employee);
    }

    return {
      date: day.date,
      weekend: day.weekend,
      holiday: day.holiday,
      tiles,
      staffed: staffed.size,
      absent,
      unplanned,
    };
  });

  return { columns, employeesById, sitesById };
}

function cellKey(date: string, siteId: number): string {
  return `${date}|${siteId}`;
}

/**
 * Einteilungen einer Kachel nach Kolonnen bündeln. Die Kolonne erscheint in der
 * Kachel als eingerahmte Einheit; ihre Namen bleiben trotzdem einzeln bedienbar
 * (austragen, antippen, ziehen).
 *
 * Den Rahmen gibt es nur, wo die Kolonne als Gruppe steht (mindestens zwei
 * Mitglieder): wer aus seiner Kolonne gelöst und woanders eingeteilt wird,
 * steht dort ohne Kolonnen-Markierung — wie freie Mitarbeiter auch
 * (Vorgabe des Nutzers, 13.08.2026).
 */
function groupEntriesByCrew(entries: PlanEntry[], crews: Crew[]): TileCrewGroup[] {
  const groups: TileCrewGroup[] = [];
  const framed = new Set<number>();

  for (const crew of crews) {
    const members = entries.filter((entry) => entry.employee.crewId === crew.id);
    if (members.length < 2) continue;
    groups.push({ crew, entries: members });
    for (const entry of members) framed.add(entry.assignment.id);
  }

  const loose = entries.filter((entry) => !framed.has(entry.assignment.id));
  if (loose.length > 0) groups.push({ crew: null, entries: loose });

  return groups;
}

function byName(a: PlanEntry, b: PlanEntry): number {
  return a.employee.displayName.localeCompare(b.employee.displayName, 'de');
}

function groupBy<T>(items: T[], key: (item: T) => number): Map<number, T[]> {
  const map = new Map<number, T[]>();
  for (const item of items) {
    const id = key(item);
    const list = map.get(id);
    if (list) list.push(item);
    else map.set(id, [item]);
  }
  return map;
}

/** Wo steht die Person an diesem Tag sonst noch? Zeigt Doppelbelegungen. */
export function otherSitesOnDay(
  column: DayColumn | undefined,
  employeeId: number,
  exceptSiteId: number,
): string[] {
  if (!column) return [];
  return column.tiles
    .filter(
      (tile) =>
        tile.site.id !== exceptSiteId &&
        tile.entries.some((entry) => entry.employee.id === employeeId),
    )
    .map((tile) => tile.site.name);
}

/** Beschreibung einer Kachel für Vorlese- und Hilfstechnik. */
export function describeTile(tile: SiteTile, date: string): string {
  if (tile.entries.length === 0) return `${tile.site.name}, ${date} — niemand eingeteilt`;
  return `${tile.site.name}, ${date} — ${tile.entries
    .map((entry) => entry.employee.displayName)
    .join(', ')}`;
}
